import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MetricsCard from './components/MetricsCard';
import ProjectionTable from './components/ProjectionTable';
import StrategicRecommendations from './components/StrategicRecommendations';
import ExportActions from './components/ExportActions';
import TrustSignals from './components/TrustSignals';
import EmailGateModal from '../../components/ui/EmailGateModal';
import RevenueTrendChart from './components/RevenueTrendChart';
import { emailService } from '../../utils/emailService';

const FinancialProjections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projections, setProjections] = useState([]);
  const [keyMetrics, setKeyMetrics] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Enhanced form validation function
  const validateCalculatorData = (formData) => {
    if (!formData) return false;
    
    // Check required sections (same validation as in calculator)
    const isComplete = (
      // Site Inventory - Required
      (formData?.siteInventory?.shortTermSites > 0 || formData?.siteInventory?.longTermSites > 0) &&
      // Location - Required  
      formData?.location?.address?.trim() &&
      // Utilities - At least one required
      (formData?.utilities?.power || formData?.utilities?.water || formData?.utilities?.sewer) &&
      // Pricing - Valid rates required
      formData?.pricing?.nightly > 0 &&
      // Operational - Required
      formData?.operational?.daysOpenPerMonth > 0 &&
      formData?.operational?.occupancyRate > 0
    );
    
    return isComplete;
  };

  // Enhanced access validation with localStorage fallback
  const validateAccessAndData = () => {
    // First try to get calculator data from route state
    let calculatorData = location?.state?.formData;
    
    // If no route state data, check localStorage
    if (!calculatorData) {
      const savedData = localStorage.getItem('rvParkCalculatorData');
      const savedCompleted = localStorage.getItem('rvParkCalculatorCompleted');
      const savedTimestamp = localStorage.getItem('rvParkCalculatorTimestamp');
      
      // Check if localStorage has valid completed data (within last 24 hours)
      if (savedData && savedCompleted && savedTimestamp) {
        try {
          const parsedData = JSON.parse(savedData);
          const isCompleted = JSON.parse(savedCompleted);
          const timestamp = new Date(savedTimestamp);
          const now = new Date();
          const hoursSinceCompletion = (now - timestamp) / (1000 * 60 * 60);
          
          // Data is valid if completed and within last 24 hours
          if (isCompleted && hoursSinceCompletion < 24 && validateCalculatorData(parsedData)) {
            calculatorData = parsedData;
          }
        } catch (error) {
          console.error('Error parsing saved calculator data:', error);
        }
      }
    }
    
    // If still no valid calculator data, redirect to calculator
    if (!validateCalculatorData(calculatorData)) {
      navigate('/rv-park-calculator');
      return null;
    }
    
    return calculatorData;
  };

  // Check access on component mount
  useEffect(() => {
    const checkAccess = () => {
      // Validate calculator completion and data
      let calculatorData = validateAccessAndData();
      if (!calculatorData) return; // Already redirected
      
      // Calculator data is complete, now check email access
      const hasAccess = emailService?.hasAccess();
      
      if (!hasAccess) {
        setShowEmailModal(true);
        setAccessChecked(true);
        return;
      }
      
      setAccessChecked(true);
      // User has access, load projections with real data
      loadProjections(calculatorData);
    };
    
    checkAccess();
  }, [navigate, location?.state?.formData]);

  // Helper function to calculate revenue breakdown using actual form data
  const calculateRevenueBreakdown = (formData) => {
    const totalSites = (formData?.siteInventory?.shortTermSites || 0) + (formData?.siteInventory?.longTermSites || 0);
    const occupancyRate = formData?.operational?.occupancyRate / 100;
    const daysOpen = formData?.operational?.daysOpenPerMonth || 30;

    // Calculate base revenue from different pricing tiers
    const shortTermRevenue = (formData?.siteInventory?.shortTermSites || 0) * (formData?.pricing?.nightly || 0) * occupancyRate * daysOpen * 0.7; // 70% short-term
    const weeklyRevenue = (formData?.siteInventory?.shortTermSites || 0) * ((formData?.pricing?.weekly || 0) / 7) * occupancyRate * daysOpen * 0.2; // 20% weekly
    const monthlyRevenue = (formData?.siteInventory?.longTermSites || 0) * ((formData?.pricing?.monthly || 0) / 30) * occupancyRate * daysOpen * 1.0; // 100% monthly

    const baseRevenue = shortTermRevenue + weeklyRevenue + monthlyRevenue;

    // Apply revenue multipliers
    let multiplierBoost = 1;
    formData?.revenueMultipliers?.forEach((multiplier) => {
      switch (multiplier) {
        case 'dynamicPricing': multiplierBoost += 0.20; break;
        case 'weekendPremium': multiplierBoost += 0.25; break;
        case 'lockSiteFees': multiplierBoost += 0.075; break;
        case 'eventPricing': multiplierBoost += 0.15; break;
        case 'earlyBooking': multiplierBoost += 0.125; break;
        case 'loyaltyProgram': multiplierBoost += 0.10; break;
        default: break;
      }
    });

    const grossRevenue = baseRevenue * multiplierBoost;

    // Calculate costs based on selected model
    let costPercentage = 0.50; // Default 50%
    switch (formData?.operational?.costModel) {
      case 'lean': costPercentage = 0.375; break; // 37.5% (middle of 30-45%)
      case 'standard': costPercentage = 0.525; break; // 52.5% (middle of 45-60%)
      case 'premium': costPercentage = 0.675; break; // 67.5% (middle of 60-75%)
      default: break;
    }

    const estimatedCosts = grossRevenue * costPercentage;
    const netIncome = grossRevenue - estimatedCosts;

    return {
      grossRevenue,
      estimatedCosts,
      netIncome,
      totalSites,
      occupancyRate: formData?.operational?.occupancyRate,
      multiplierBoost: (multiplierBoost - 1) * 100,
      formData // Include original form data for recommendations
    };
  };

  const loadProjections = async (calculatorData) => {
    setIsLoading(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Calculate real revenue breakdown using actual form data
    const breakdown = calculateRevenueBreakdown(calculatorData);
    
    // Generate 5-year projections with growth based on real data
    const baseRevenue = breakdown?.netIncome * 12; // Annual net income
    const projectionData = [];
    
    for (let year = 1; year <= 5; year++) {
      const growthRate = 2 + (year * 0.5); // 2.5% to 4.5% growth
      const growthMultiplier = Math.pow(1 + (growthRate / 100), year - 1);
      
      const grossRevenue = Math.round(breakdown?.grossRevenue * 12 * growthMultiplier); // Use real gross revenue
      const operatingCosts = Math.round(breakdown?.estimatedCosts * 12 * growthMultiplier); // Use real costs
      const netIncome = grossRevenue - operatingCosts;
      const roi = ((netIncome / grossRevenue) * 100);
      
      projectionData?.push({
        year,
        grossRevenue,
        operatingCosts,
        netIncome,
        growthRate,
        roi
      });
    }
    
    setProjections(projectionData);
    
    // Calculate key metrics
    const totalRevenue = projectionData?.reduce((sum, p) => sum + p?.grossRevenue, 0);
    const avgGrowth = projectionData?.reduce((sum, p) => sum + p?.growthRate, 0) / projectionData?.length;
    const avgROI = projectionData?.reduce((sum, p) => sum + p?.roi, 0) / projectionData?.length;
    
    setKeyMetrics({
      totalRevenue,
      avgGrowth,
      avgROI,
      breakEvenYear: 1.2
    });
    
    // Generate strategic recommendations based on actual calculator data
    const strategicRecs = generateRecommendations(calculatorData);
    setRecommendations(strategicRecs);
    
    setIsLoading(false);
  };

  const generateRecommendations = (data) => {
    const recs = [];
    
    // Revenue optimization based on hookup configuration
    const hasFullHookups = data?.utilities?.power && data?.utilities?.water && data?.utilities?.sewer;
    if (hasFullHookups) {
      recs?.push({
        category: "Revenue Optimization",
        title: "Premium Full-Hookup Positioning",
        description: `With full hookups (power, water, sewer), you can command premium rates and attract luxury RV travelers. Consider implementing tiered pricing with premium spots featuring additional amenities like cable TV, high-speed internet, and concrete pads.`,
        impact: "High",
        timeline: "3-6 months",
        expectedROI: "15-25% revenue increase",
        actionItems: [
          "Install premium amenities at select sites",
          "Implement dynamic pricing for peak seasons",
          "Market to luxury RV associations and clubs"
        ]
      });
    }
    
    // Marketing strategy based on features
    if (data?.siteFeatures?.includes("waterfront")) {
      recs?.push({
        category: "Marketing Strategy",
        title: "Waterfront Premium Marketing",
        description: `Your waterfront location is a significant competitive advantage. Develop targeted marketing campaigns highlighting water activities, scenic views, and premium positioning to justify higher rates and attract longer stays.`,
        impact: "High",
        timeline: "2-4 months",
        expectedROI: "20-30% rate premium",
        actionItems: [
          "Professional photography of waterfront sites",
          "Partner with fishing and water sports communities",
          "Create seasonal packages for water activities"
        ]
      });
    }
    
    // Operational efficiency based on occupancy
    if (data?.operational?.occupancyRate < 80) {
      recs?.push({
        category: "Operational Efficiency",
        title: "Occupancy Rate Optimization",
        description: `Your current ${data?.operational?.occupancyRate}% occupancy rate has room for improvement. Focus on marketing, guest experience, and strategic partnerships to increase utilization and revenue per site.`,
        impact: "Medium",
        timeline: "6-12 months",
        expectedROI: "10-15% revenue increase",
        actionItems: [
          "Implement guest feedback system",
          "Develop referral incentive program",
          "Partner with local attractions and events"
        ]
      });
    }
    
    // Cost management based on model
    if (data?.operational?.costModel === "standard") {
      recs?.push({
        category: "Cost Management",
        title: "Operational Cost Optimization",
        description: `With standard cost management, there are opportunities to reduce expenses through energy efficiency, bulk purchasing, and automation. Focus on high-impact areas like utilities and maintenance.`,
        impact: "Medium",
        timeline: "4-8 months",
        expectedROI: "5-10% cost reduction",
        actionItems: [
          "Audit utility usage and implement energy-saving measures",
          "Negotiate bulk purchasing agreements for supplies",
          "Automate reservation and check-in processes"
        ]
      });
    }
    
    // Site inventory optimization
    const totalSites = (data?.siteInventory?.shortTermSites || 0) + (data?.siteInventory?.longTermSites || 0);
    if (totalSites > 0) {
      const shortTermRatio = (data?.siteInventory?.shortTermSites || 0) / totalSites;
      if (shortTermRatio > 0.7) {
        recs?.push({
          category: "Revenue Strategy",
          title: "Short-Term Focus Optimization",
          description: `With ${Math.round(shortTermRatio * 100)}% short-term sites, focus on maximizing nightly rates during peak seasons and consider dynamic pricing strategies to capture demand fluctuations.`,
          impact: "High",
          timeline: "3-6 months",
          expectedROI: "12-20% revenue increase",
          actionItems: [
            "Implement seasonal pricing tiers",
            "Partner with local event organizers",
            "Optimize online booking platforms"
          ]
        });
      }
    }
    
    // Location-based recommendations
    if (data?.location?.address) {
      recs?.push({
        category: "Market Positioning",
        title: "Location-Based Marketing Strategy",
        description: `Leverage your location in ${data?.location?.address} to target specific demographics and seasonal travelers. Develop partnerships with local attractions and create location-specific marketing campaigns.`,
        impact: "Medium",
        timeline: "4-8 months",
        expectedROI: "8-15% revenue increase",
        actionItems: [
          "Research local tourism patterns and events",
          "Create location-specific marketing materials",
          "Develop partnerships with nearby attractions"
        ]
      });
    }
    
    return recs;
  };

  const handleExport = (type) => {
    // Mock export success notification
    console.log(`Successfully exported projections as ${type}`);
  };

  const handleEmailSuccess = (email, isExisting) => {
    setShowEmailModal(false);
    // Get calculator data and load projections
    let calculatorData = validateAccessAndData();
    if (calculatorData) {
      loadProjections(calculatorData);
    }
  };

  // Enhanced handleModalClose to redirect to calculator
  const handleModalClose = () => {
    // If user closes modal without providing email, redirect to calculator
    setShowEmailModal(false);
    navigate('/rv-park-calculator');
  };

  // Show loading screen while checking access
  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <h2 className="font-heading font-semibold text-lg text-foreground">
                Checking Access...
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show email gate modal if no access
  if (showEmailModal) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Teaser Content */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Financial Projections
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Unlock comprehensive 5-year financial forecasts and personalized business strategies 
                based on your RV park configuration.
              </p>
            </div>

            {/* Preview Cards - Blurred */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 relative">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
                <div className="text-center space-y-4">
                  <Icon name="Lock" size={48} color="var(--color-primary)" />
                  <h3 className="text-xl font-bold text-foreground">Content Locked</h3>
                  <p className="text-muted-foreground">Enter your email to access detailed projections</p>
                  <Button onClick={() => setShowEmailModal(true)} iconName="Unlock" iconPosition="left">
                    Unlock Now
                  </Button>
                </div>
              </div>
              
              {/* Blurred Preview Cards */}
              <div className="bg-card border border-border rounded-lg p-6 blur-sm">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-full"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 blur-sm">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-full"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 blur-sm">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-full"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 blur-sm">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-8 bg-muted rounded w-full"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Gate Modal */}
        <EmailGateModal
          isOpen={showEmailModal}
          onClose={handleModalClose}
          onSuccess={handleEmailSuccess}
          title="Access Financial Projections"
        />
      </div>
    );
  }

  // Show loading screen while generating projections
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <h2 className="font-heading font-semibold text-lg text-foreground">
                Generating Your Financial Projections
              </h2>
              <p className="font-body text-muted-foreground max-w-md">
                Analyzing your RV park configuration and calculating 5-year projections with personalized strategies...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show full projections page (user has access)
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Financial Projections - RV Park Income Calculator</title>
        <meta name="description" content="Comprehensive 5-year financial projections and strategic recommendations for your RV park investment." />
      </Helmet>
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center space-x-3 mb-2">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                <span className="text-sm text-success font-medium">
                  Access Granted to {emailService?.getStoredEmail()}
                </span>
              </div>
              <h1 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-2">
                Financial Projections
              </h1>
              <p className="font-body text-lg text-muted-foreground max-w-2xl">
                Comprehensive 5-year financial forecasts and personalized business strategies based on your RV park configuration.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <ExportActions projections={projections} onExport={handleExport} />
            </div>
          </div>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricsCard
              title="Total 5-Year Revenue"
              value={new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })?.format(keyMetrics?.totalRevenue)}
              subtitle="Projected gross revenue"
              icon="DollarSign"
              trend="up"
              trendValue="+23%"
            />
            
            <MetricsCard
              title="Average Growth Rate"
              value={`${keyMetrics?.avgGrowth?.toFixed(1)}%`}
              subtitle="Annual revenue growth"
              icon="TrendingUp"
              trend="up"
              trendValue="Steady"
            />
            
            <MetricsCard
              title="Average ROI"
              value={`${keyMetrics?.avgROI?.toFixed(1)}%`}
              subtitle="Return on investment"
              icon="Target"
              trend="up"
              trendValue="Strong"
            />
            
            <MetricsCard
              title="Break-Even Point"
              value={`${keyMetrics?.breakEvenYear} Years`}
              subtitle="Time to profitability"
              icon="Clock"
              trend="up"
              trendValue="Fast"
            />
          </div>

          {/* Revenue Trend Chart */}
          <RevenueTrendChart 
            data={projections.map(p => ({
              year: `Year ${p.year}`,
              grossRevenue: p.grossRevenue,
              netIncome: p.netIncome,
              operatingCosts: p.operatingCosts
            }))} 
          />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-2 space-y-8">
              {/* Projections Table */}
              <ProjectionTable projections={projections} />
              
              {/* Strategic Recommendations */}
              <StrategicRecommendations recommendations={recommendations} />
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <div className="sticky top-24 space-y-6">
                <TrustSignals />
                
                {/* Action Panel */}
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-4">
                    Next Steps
                  </h3>
                  <div className="space-y-4">
                    <Link to="/rv-park-calculator">
                      <Button variant="outline" fullWidth iconName="Calculator" iconPosition="left">
                        Modify Calculator
                      </Button>
                    </Link>
                    
                    <a href={import.meta.env.VITE_CALENDAR_URL || "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ336m2OKF1taDCNLMvLTrBiGb593oS7huFh8A49R_GyPf7YVIB46-ifO5s3zBoq3s6NoSkI92Ll"} target="_blank" rel="noopener noreferrer">
                      <Button variant="default" fullWidth iconName="Phone" iconPosition="left">
                        Schedule Consultation
                      </Button>
                    </a>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-border">
                    <p className="font-body text-xs text-muted-foreground text-center">
                      Questions about your projections? Our RV park investment specialists are here to help.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 bg-primary/5 border border-primary/20 rounded-lg p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <Icon name="Lightbulb" size={48} className="text-primary mx-auto mb-4" />
              <h2 className="font-heading font-bold text-2xl text-foreground mb-3">
                Ready to Turn Your Land Into Income?
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                These projections are based on industry data and your specific inputs. Get personalized guidance from our RV park development experts to maximize your investment potential.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={import.meta.env.VITE_CALENDAR_URL || "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ336m2OKF1taDCNLMvLTrBiGb593oS7huFh8A49R_GyPf7YVIB46-ifO5s3zBoq3s6NoSkI92Ll"} target="_blank" rel="noopener noreferrer">
                  <Button variant="default" size="lg" iconName="Calendar" iconPosition="left">
                    Book Free Consultation
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialProjections;